import { Title, Text, Stack, Paper, Button, Group, Alert, Loader, Badge } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { IconCircleOff, IconInfoCircle } from '@tabler/icons-react'
import { useMedplum } from '@medplum/react'
import { normalizeErrorString } from '@medplum/core'
import { Navigation } from './components'
import { useClient, type OrpcClient } from '@/hooks/useClient'

type ConnectInfo = Awaited<ReturnType<OrpcClient['getOrganizationConnect']>>

const getReferenceId = (reference?: string): string | undefined => {
  if (!reference) return undefined
  const parts = reference.split('/')
  return parts[parts.length - 1]
}

export function OrganizationConnectAccept() {
  const medplum = useMedplum()
  const client = useClient()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const profile = medplum.getProfile()

  const [isRoleLoading, setIsRoleLoading] = useState(true)
  const [organizationId, setOrganizationId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [connect, setConnect] = useState<ConnectInfo | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isAccepting, setIsAccepting] = useState(false)
  const [isDeclining, setIsDeclining] = useState(false)

  useEffect(() => {
    let isActive = true
    const loadRole = async () => {
      setIsRoleLoading(true)
      try {
        if (!profile?.id) {
          if (isActive) {
            setOrganizationId(null)
            setIsRoleLoading(false)
          }
          return
        }
        const role = await medplum.searchOne(
          'PractitionerRole',
          `practitioner=Practitioner/${profile.id}`,
        )
        if (isActive) {
          setOrganizationId(getReferenceId(role?.organization?.reference) ?? null)
        }
      } catch (err) {
        if (!isActive) return
        showNotification({
          color: 'red',
          icon: <IconCircleOff />,
          title: 'Error',
          message: normalizeErrorString(err) || 'Failed to load practitioner role.',
        })
        setOrganizationId(null)
      } finally {
        if (isActive) setIsRoleLoading(false)
      }
    }
    loadRole()
    return () => {
      isActive = false
    }
  }, [medplum, profile?.id])

  useEffect(() => {
    let isActive = true
    const load = async () => {
      if (!id) return
      setIsLoading(true)
      setLoadError(null)
      try {
        const result = await client.getOrganizationConnect({ id })
        if (isActive) setConnect(result)
      } catch (err) {
        if (isActive) {
          setLoadError(
            normalizeErrorString(err) || 'Connection request not found or no longer available.',
          )
        }
      } finally {
        if (isActive) setIsLoading(false)
      }
    }
    load()
    return () => {
      isActive = false
    }
  }, [client, id])

  const handleAccept = async () => {
    if (!id) return
    setIsAccepting(true)
    try {
      await client.acceptOrganizationConnect({ id })
      showNotification({
        color: 'green',
        title: 'Connection accepted',
        message: 'You are now connected with the inviting organization.',
      })
      navigate('/settings/connects')
    } catch (err) {
      showNotification({
        color: 'red',
        title: 'Failed to accept connection',
        message: normalizeErrorString(err) || 'Unable to accept the connection request.',
      })
    } finally {
      setIsAccepting(false)
    }
  }

  const handleDecline = async () => {
    if (!id) return
    setIsDeclining(true)
    try {
      await client.declineOrganizationConnect({ id })
      showNotification({
        color: 'gray',
        title: 'Connection declined',
        message: '',
      })
      navigate('/settings/connects')
    } catch (err) {
      showNotification({
        color: 'red',
        title: 'Failed to decline connection',
        message: normalizeErrorString(err) || 'Unable to decline the connection request.',
      })
    } finally {
      setIsDeclining(false)
    }
  }

  return (
    <>
      <Navigation />
      <Stack className="mx-auto w-full max-w-[700px] p-6" gap="lg">
        <Title order={2}>Connection request</Title>

        {isRoleLoading || isLoading ? (
          <Group>
            <Loader size="sm" />
            <Text size="sm">Loading…</Text>
          </Group>
        ) : !organizationId ? (
          <Alert icon={<IconInfoCircle />} color="yellow">
            Sign in with an organization account to review and accept this connection request.
          </Alert>
        ) : loadError ? (
          <Alert icon={<IconInfoCircle />} color="red">
            {loadError}
          </Alert>
        ) : !connect ? (
          <Alert icon={<IconInfoCircle />} color="red">
            Connection request not found.
          </Alert>
        ) : connect.status !== 'pending' ? (
          <Paper withBorder p="md">
            <Stack>
              <Badge color="gray">Already {connect.status}</Badge>
              <Text>
                This connection request has already been processed and cannot be changed here.
              </Text>
              <Group justify="flex-end">
                <Button variant="default" onClick={() => navigate('/settings/connects')}>
                  Back to connections
                </Button>
              </Group>
            </Stack>
          </Paper>
        ) : (
          <Paper withBorder p="md">
            <Stack>
              <Text>
                <strong>{connect.initiator.name}</strong> wants to connect with your organization on
                BeHuman.
              </Text>
              <Text c="dimmed" size="sm">
                Accepting the connection lets both organizations coordinate care across patient
                referrals in the future. You can remove the connection at any time.
              </Text>
              <Group justify="flex-end">
                <Button
                  variant="default"
                  color="red"
                  loading={isDeclining}
                  onClick={handleDecline}
                  disabled={isAccepting}
                >
                  Decline
                </Button>
                <Button loading={isAccepting} onClick={handleAccept} disabled={isDeclining}>
                  Accept
                </Button>
              </Group>
            </Stack>
          </Paper>
        )}
      </Stack>
    </>
  )
}
